import { useEffect, useRef, forwardRef } from 'react';
import { cn } from '../lib/utils';

/**
 * AutoGrowTextarea
 * ─────────────────
 * Drop-in replacement for a raw <textarea>. No character/word limit, no
 * fixed height cap. Grows automatically as the user types, with a ~6 visible
 * line minimum (matches the universal "Description / Notes" spec).
 *
 * Use this for every Description / Notes / Reason / Comment field in the
 * CRM. The shared shadcn <Textarea> in components/ui/textarea.jsx also
 * inherits the same behavior via the same hook.
 */
export function useAutoGrow(value) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to recompute scrollHeight on shrink as well as grow.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return ref;
}

export const AutoGrowTextarea = forwardRef(function AutoGrowTextarea(
  { value, onChange, className, minRows = 6, ...props },
  forwardedRef,
) {
  const internalRef = useRef(null);
  const setRef = (node) => {
    internalRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  // Resize on value change.
  useEffect(() => {
    const el = internalRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  // Resize on mount + when window resizes (responsive shrink/grow).
  useEffect(() => {
    const el = internalRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  return (
    <textarea
      ref={setRef}
      value={value}
      onChange={onChange}
      rows={minRows}
      className={cn(
        'w-full text-[13px] bg-foreground/[0.02] border border-border/30 rounded-lg px-3 py-2 outline-none resize-none focus:border-gold/40 overflow-hidden',
        className,
      )}
      {...props}
    />
  );
});

export default AutoGrowTextarea;
